import React, { useState, useEffect, ReactElement } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import mustache from 'mustache';
import { clsx } from 'clsx';
import { useTranslations } from '../../../../hooks/useTranslations';
import { MediumHeader, SemibigHeader } from '../../../ui/Header/Header';
import { Item } from '../../../../../../__generated__/loma';
import Paragraph from '../../../ui/Paragraph/Paragraph';
import TextLink from '../../../ui/TextLink/TextLink';
import Button from '../../../ui/Button/BlockButton';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
} from '../../../../hooks/Analytics';
import { useSettings } from '../../../../hooks/useSettings';
import { useTheme } from '../../../../hooks/useTheme';
import { CategoryConditionCategory } from '../../DynamicLayout/layoutTree';
import useOrderTracking, {
  OrderTrackingQueryData,
} from '../../../../hooks/useOrderTracking';
import ItemImage, { imageWouldRenderEmpty } from './ItemImage';
import { ItemTile } from '../../ItemsInThisShipment';
import TrackingStatusText from './TrackingStatusText';
import { usePageContext } from '../../../../hooks/PageContext/usePageContext';
import { useGlobalSettings } from '../../../../hooks/useGlobalSettings';
import styles from './ShipmentRow.module.css';

enum DateTokenType {
  DAY_OF_MONTH = 'DAY_OF_MONTH',
  DAY_OF_WEEK = 'DAY_OF_WEEK',
  MONTH = 'MONTH',
}

type ShipmentRowData =
  OrderTrackingQueryData['orderTracking']['orders'][0]['shipments'][0] & {
    displayDateFormatted: string[];
  };

const renderOptions = { escape: (x: string): string => x };

export interface ShipmentRowProps {
  landscape: boolean;
  featureName?: string;
  index: number;
  shipment: ShipmentRowData;
  carouselScrolls?: number;
}

const ShipmentRow = ({
  landscape,
  featureName,
  index,
  shipment,
  carouselScrolls = 0,
}: ShipmentRowProps) => {
  const logEvent = useAnalyticsPost();
  const { state } = usePageContext();
  const { setCurrentShipment } = useOrderTracking();
  const { category, mainShipmentId } = state;
  const [showItemDetails, setShowItemDetails] = useState(false);

  const {
    status,
    items,
    displayDateFormatted,
    statusCode,
    subStatus,
    shipmentId,
  } = shipment;

  // The border of each shipment should match the styles from panelcontainerNoneNone
  // The padding of each shipment should match the padding of the panelbodyNoneNone,
  // since `Shipments` sets that padding to 0 so that the bottom border of each shipment
  // can extend to the edge of the panel container. So we want each shipment to inherit
  // that padding, instead of having it defined on the inner container of panel
  const {
    panelcontainerNoneNone: {
      borderTopColor,
      borderTopStyle,
      borderTopWidth,
    } = {
      borderTopColor: undefined,
      borderTopStyle: undefined,
      borderTopWidth: undefined,
    },
    linksNoneDefault,
    linksNoneHover,
  } = useTheme();

  const {
    shipmentsItemLabel,
    shipmentsButton,
    packShipmentsDateTokenOrder,
    packShipmentsMoreDetails,
    packShipmentsHideDetails,
    itemsInShipmentItemTitle,
    itemsInShipmentAttribute0,
    itemsInShipmentAttribute1,
    itemsInShipmentAttribute2,
    itemsInShipmentAttribute3,
  } = useTranslations();

  const itemTextTemplates = {
    itemsInShipmentItemTitle,
    itemsInShipmentAttribute0,
    itemsInShipmentAttribute1,
    itemsInShipmentAttribute2,
    itemsInShipmentAttribute3,
  };

  const { shipments } = useSettings();

  const { internationalDateFormatting, giftSharingEnabled } =
    useGlobalSettings();

  const updatePageMainShipment = () => {
    if (!shipmentId) return;
    setCurrentShipment(shipmentId);
    // after updating page shipment info, reset page focus for a11y.
    if (typeof window === 'object') {
      window.scroll({ top: 0 });
      document.body.focus();
    }
  };

  // hide item details whenever the shipments carousel scrolls
  useEffect(() => {
    setShowItemDetails(false);
  }, [carouselScrolls]);

  const [dayOfWeek, month, dayOfMonth] = displayDateFormatted;

  const displayDateWithTokens = {
    [DateTokenType.DAY_OF_WEEK]: dayOfWeek,
    [DateTokenType.MONTH]: month,
    [DateTokenType.DAY_OF_MONTH]: dayOfMonth,
  };

  const formattedOrderedDate =
    internationalDateFormatting &&
    typeof packShipmentsDateTokenOrder === 'string'
      ? (packShipmentsDateTokenOrder.split(',') as DateTokenType[]).map(
          (dateToken) => {
            if (dateToken in DateTokenType)
              return displayDateWithTokens[dateToken];
            return null;
          },
        )
      : displayDateFormatted;

  const paragraphStyles = {
    marginTop: 0,
    marginBottom: '.5rem',
    lineHeight: '1.25rem',
  };

  const { imageUrl: imageUrlTemplate, url: urlTemplate = '' } = shipments ?? {};

  const { totalItemsQuantity, itemImages } = (items || []).reduce(
    (
      accum: { totalItemsQuantity: number; itemImages: ReactElement[] },
      item,
      index,
    ) => {
      if (!item) return accum;

      // Currently the "hack" to not show the product images is setting the
      // image template setting to ' '. In an upcoming refresh of the shipments
      // component settings, there will be a boolean flag
      // we'll be able to check instead of looking for ' '.
      const hideProductImage = imageUrlTemplate === ' ';

      const itemImage =
        hideProductImage ||
        imageWouldRenderEmpty(item, imageUrlTemplate) ? null : (
          <ItemImage
            featureName={featureName}
            item={item}
            key={`${item.name || item.imageUrl || item.url || ''}_${index}`}
            urlTemplate={urlTemplate}
            imageUrlTemplate={imageUrlTemplate}
          />
        );

      return {
        totalItemsQuantity: accum.totalItemsQuantity + (item.quantity || 0),
        itemImages: itemImage
          ? [...accum.itemImages, itemImage]
          : accum.itemImages,
      };
    },
    {
      totalItemsQuantity: 0,
      itemImages: [],
    },
  );

  const moreDetailsToggle = (
    <div className={styles.moreDetailsContainer}>
      <TextLink
        linkStyle={{
          sDefault: linksNoneDefault,
          sHover: linksNoneHover,
        }}
        onClick={() => setShowItemDetails(!showItemDetails)}
        aria-expanded={showItemDetails}
        aria-controls={`shipment-item-details-${shipment.shipmentId ?? ''}`}
      >
        {showItemDetails ? packShipmentsHideDetails : packShipmentsMoreDetails}
      </TextLink>
    </div>
  );

  const itemChunks = items.reduce(
    (allItemChunks: [Item, Item?][], _, index: number, arr) => {
      if (index % 2 === 0) {
        const [itemChunk0, itemChunk1] = arr.slice(index, index + 2);
        if (itemChunk0) {
          allItemChunks.push([itemChunk0, itemChunk1]);
        }
      }
      return allItemChunks;
    },
    [],
  );

  const itemTiles = (
    <div
      className={styles.itemDetailsContainer}
      id={`shipment-item-details-${shipment?.shipmentId ?? ''}`}
      key={`shipment-item-tiles-${shipment?.shipmentId ?? ''}`}
    >
      {(Array.isArray(itemChunks) ? itemChunks : []).map(
        ([itemChunk0, itemChunk1]) => (
          <div
            className={styles.shipmentItemContainer}
            key={`${itemChunk0?.productSku ?? ''} ${
              itemChunk1?.productSku ?? ''
            }`}
          >
            <div className={styles.shipmentItem}>
              <ItemTile
                itemInfo={itemChunk0}
                itemTextTemplates={itemTextTemplates}
                featureName={featureName}
              />
            </div>
            {itemChunk1 && (
              <div className={styles.shipmentItem}>
                <ItemTile
                  itemInfo={itemChunk1}
                  itemTextTemplates={itemTextTemplates}
                  featureName={featureName}
                />
              </div>
            )}
          </div>
        ),
      )}
    </div>
  );

  const shipmentContainerStyles = clsx({
    [styles.shipmentContainer]: !landscape,
    [styles.shipmentContainerLandscape]: landscape,
  });

  const shipmentStatusStyles = clsx({
    [styles.shipmentStatus]: true,
    [styles.shipmentStatusLandscape]: landscape,
    [styles.shipmentStatusEddPresent]: !!(month && dayOfWeek && dayOfMonth),
  });

  const itemsInShipmentStyles = clsx({
    [styles.itemsInShipment]: true,
    [styles.itemsInShipmentLandscape]: landscape,
  });

  const buttonContainerStyles = clsx({
    [styles.buttonContainer]: true,
    [styles.buttonContainerLandscape]: landscape,
  });

  const isGiftExperience =
    giftSharingEnabled && category === CategoryConditionCategory.SHARING;
  const showItemImagesLabel = totalItemsQuantity > 1 && !isGiftExperience;
  const showItemImages = itemImages.length > 0 && !isGiftExperience;

  const isCurrentShipment = shipmentId === mainShipmentId;

  // for noflake: the new track url clicking the button will eventually navigate to
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentSearchParams = new URLSearchParams(searchParams?.toString());
  currentSearchParams.set('tracking_numbers', shipmentId ?? '');
  const trackShipmentUrl = `${pathname}?${currentSearchParams.toString()}`;

  return (
    <>
      <div
        className={shipmentContainerStyles}
        style={{ borderTopColor, borderTopStyle, borderTopWidth }}
        data-testid="shipment-root"
      >
        <div className={shipmentStatusStyles}>
          {status && (
            <MediumHeader
              as="p"
              containerCustomStyle={{
                wordBreak: 'break-word',
                marginBottom:
                  landscape && !(month && dayOfWeek && dayOfMonth)
                    ? undefined
                    : '1rem',
                lineHeight: 1,
              }}
            >
              <TrackingStatusText
                status={status ?? undefined}
                statusCode={statusCode ?? undefined}
                subStatus={subStatus ?? undefined}
              />
            </MediumHeader>
          )}
          {formattedOrderedDate[0] &&
            formattedOrderedDate[1] &&
            formattedOrderedDate[2] && (
              <SemibigHeader
                as="p"
                containerCustomStyle={{
                  wordBreak: 'break-word',
                  marginBottom: landscape ? undefined : '1rem',
                  lineHeight: 1,
                }}
              >
                {`${formattedOrderedDate[0]}, ${formattedOrderedDate[1]} ${formattedOrderedDate[2]}`}
              </SemibigHeader>
            )}
        </div>
        <div className={itemsInShipmentStyles}>
          {showItemImagesLabel && (
            <Paragraph
              id={`nth-pack-shipments-image-list-${index}`}
              containerCustomStyle={paragraphStyles}
            >
              {mustache.render(
                shipmentsItemLabel || '',
                {
                  itemsCount: totalItemsQuantity,
                  shipmentNumber: shipment.trackingNumber,
                },
                undefined,
                renderOptions,
              )}
            </Paragraph>
          )}
          {showItemImages && (
            <ul
              className={styles.imagesContainer}
              data-testid="images-container"
              aria-labelledby={
                showItemImagesLabel
                  ? `nth-pack-shipments-image-list-${index}`
                  : undefined
              }
              aria-label={
                !showItemImagesLabel
                  ? mustache.render(
                      shipmentsItemLabel || '',
                      {
                        itemsCount: totalItemsQuantity,
                        shipmentNumber: shipment.trackingNumber,
                      },
                      undefined,
                      renderOptions,
                    )
                  : undefined
              }
            >
              {itemImages}
            </ul>
          )}
          {!landscape && showItemDetails && itemTiles}
          {!isGiftExperience && moreDetailsToggle}
        </div>
        {status !== 'PROCESSING' && !isCurrentShipment && (
          <div
            className={buttonContainerStyles}
            data-testid="shipment-button-container"
          >
            <Button
              big={!landscape}
              onClick={(): void => {
                logEvent({
                  clickCategory: NarvarClickSubTypes.NAVIGATION_ELEMENT,
                  clickLinkDestinationUrl: trackShipmentUrl,
                  clickLinkLabel: shipmentsButton,
                  clickSubType: NarvarClickSubTypes.SHIPMENTS,
                  clickType: NarvarClickTypes.INTERACTION,
                  eventComponent: NarvarEventComponents.NTH_PACK_SHIPMENTS,
                  eventFeature: NarvarEventComponents.NTH_PACK_SHIPMENTS,
                  eventType: NarvarEventTypes.CLICK,
                  feature: featureName,
                  pageComponent: NarvarEventComponents.NTH_PACK_SHIPMENTS,
                });
                updatePageMainShipment();
              }}
            >
              {shipmentsButton}
            </Button>
          </div>
        )}
      </div>
      {landscape && showItemDetails && itemTiles}
    </>
  );
};

export default ShipmentRow;
